import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext } from 'next';

import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { MarkdownWithHtml } from '~/components/markdown-with-html';
import { PageCategory } from '~/features/analytics/constants';
import { languages } from '~/features/internationalization/languages';
import { LegalTableOfContents } from '~/features/legal/components/legal-table-of-contents';
import { getLegalDocumentContent } from '~/features/legal/controllers/get-legal-document-content';
import { LegalDocumentContent } from '~/features/legal/models/legal-document-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { format, useFormatDate } from '~/hooks/use-format-date';

export default function LegalDocumentPage({
  page,
  legalDocumentContent,
}: {
  page: Page;
  legalDocumentContent: LegalDocumentContent;
}) {
  const documentContentContainerRef = useRef<HTMLDivElement>(null);
  const markdownText = legalDocumentContent.body ?? '';
  const formattedDate = useFormatDate({
    date: legalDocumentContent._updatedAt,
    format: format.monthDayYear,
    forceUTC: true,
  });

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Legal} />
      <PageLayout withoutVerticalPadding>
        <Box
          bgColor='grey.50'
          padding={{
            base: '24px',
            md: '40px',
          }}
        >
          <ContentContainer>
            <Grid
              columnGap={{ base: '0px', md: '48px', lg: '96px' }}
              gridTemplateColumns={{ base: '1fr', md: '256px 1fr' }}
              gridTemplateRows={{ base: '1fr', md: 'auto 1fr' }}
              rowGap='40px'
              templateAreas={{
                base: `
              "header"
              "tableOfContents"
              "markdownContent"
            `,
                md: `
              "tableOfContents header"
              "tableOfContents markdownContent"
            `,
              }}
            >
              <GridItem area={'tableOfContents'}>
                <LegalTableOfContents
                  documentContentContainerRef={documentContentContainerRef}
                  markdownText={markdownText}
                />
              </GridItem>
              <GridItem area={'header'}>
                <Box alignItems='center' display='flex'>
                  <Glyph height={48} name='clipboard-two' width={48} />
                  <Box>
                    <Heading as='h1' size='h1'>
                      {legalDocumentContent.pageTitle}
                    </Heading>
                    <Text size='caption'>Last Updated: {formattedDate}</Text>
                  </Box>
                </Box>
              </GridItem>
              <GridItem area={'markdownContent'}>
                <Box
                  ref={documentContentContainerRef}
                  sx={{
                    '.ureact-markdown > h2': { scrollMarginTop: '64px' },
                    '.ureact-markdown > h2:first-child': { marginTop: '0px' },
                  }}
                >
                  <MarkdownWithHtml markdownText={markdownText} />
                </Box>
              </GridItem>
            </Grid>
          </ContentContainer>
        </Box>
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: [],
  };
}

export async function getStaticProps({ params, locale }: GetStaticPropsContext<{ slug: string }>) {
  const legalDocumentSlug = params?.slug;

  if (!legalDocumentSlug) {
    logger.fatal('Legal slug not found', {
      file: 'src/pages/legal/[slug].tsx',
      function: 'getStaticProps',
      legalDocumentSlug,
    });

    throw new Error('Legal slug not found');
  }

  if (!locale) {
    logger.fatal('Legal locale not found', {
      file: 'src/pages/legal/[slug].tsx',
      function: 'getStaticProps',
      locale,
    });

    throw new Error('Locale not found');
  }

  const [page, { legalDocumentContent, isFallbackContent }] = await Promise.all([
    getPage('/legal'),
    getLegalDocumentContent(legalDocumentSlug, locale),
  ]);

  const canonicalUrl = isFallbackContent
    ? `https://www.udacity.com/${languages.enUS.id}/legal/${legalDocumentSlug}`
    : `https://www.udacity.com/${locale}/legal/${legalDocumentSlug}`;

  page.seo = { ...page.seo, canonicalUrl };

  return {
    props: {
      page,
      legalDocumentContent,
    },
  };
}
